import { DateHelpers, isShiftDateTimeInPast, isValidTimeRange } from '@utils/helpers/dateHelpers';
import { validatePhone } from '@utils/helpers/textHelpers';

/**
 * Shift form constants and validation schemas
 */

// Form step constants
export const FORM_STEPS = {
  DETAILS: 'details',
  CONTACTS: 'contacts',
  APROVE: 'aprrove',
} as const;

// Required fields for validation
export const REQUIRED_FIELDS = ['group', 'shift_date', 'time_from', 'time_till'] as const;

// Initial form data structure
export const INITIAL_FORM_DATA = {
  group: '',
  shift_date: '',
  time_from: '',
  time_till: '',
  contact_email: '',
  contact_phone: '',
  school_enabled_whatsapp: true,
} as const;

// Validation schema for shift forms
export const shiftValidationSchema = {
  details: {
    required: REQUIRED_FIELDS,
    validators: {
      shift_date: (value: string) => DateHelpers.isFutureDate(value),
      time_from: (value: string, formData?: ShiftFormData) => {
        if (!value) return false;

        // Check if shift date/time is in the past (only if we have the date)
        if (formData?.shift_date) {
          return !isShiftDateTimeInPast(formData.shift_date, value);
        }
        return true;
      },
      time_till: (value: string, formData?: ShiftFormData) => {
        if (!value) return false;

        // Check if end time is after start time
        if (formData?.time_from) {
          return isValidTimeRange(formData.time_from, value);
        }

        return true;
      },
    },
  },
  contacts: {
    validators: {
      contact_phone: (value: string) => !value || validatePhone(value), // Optional but must be valid if provided
    },
  },
} as const;

// Types
export interface ShiftFormData {
  group: string;
  shift_date: string;
  time_from: string;
  time_till: string;
  contact_email: string;
  contact_phone: string;
  school_enabled_whatsapp: boolean;
}

export type FormStep = 'details' | 'contacts' | 'aprrove';

export interface FormErrors {
  [key: string]: string;
}

// Form reducer action types
export type FormAction =
  | { type: 'UPDATE_FIELD'; field: keyof ShiftFormData; value: string | boolean }
  | { type: 'RESET_FORM' };
